import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from 'react-i18next';

interface CustomCalendarProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date | null) => void;
  eventDates: string[]; // e.g., ["2026-06-18", "2026-06-20"]
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({ selectedDate, onDateSelect, eventDates }) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const localeMap: { [key: string]: string } = {
    tr: 'tr-TR',
    en: 'en-US',
    ar: 'ar-SA',
    krm: 'ku-TR',
  };

  const dayNamesMap: { [key: string]: string[] } = {
    tr: ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"],
    en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
    ar: ["إث", "ثلا", "أر", "خم", "جم", "سب", "أح"],
    krm: ["Dş", "Sş", "Çş", "Pş", "În", "Şe", "Yş"],
  };

  const formattedMonth = currentMonth.toLocaleDateString(localeMap[language] || 'tr-TR', {
    month: 'long',
    year: 'numeric'
  });
  const dayNames = dayNamesMap[language] || dayNamesMap.tr;

  // Adjust first day to start on Monday
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  return (
    <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-brand-purple-50 sticky top-32">
      <div className="flex justify-between items-center mb-6">
        <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <svg className="w-5 h-5 text-brand-purple-900" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <h2 className="text-xl font-bold text-brand-purple-900 font-serif">
          {formattedMonth}
        </h2>
        <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <svg className="w-5 h-5 text-brand-purple-900" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2 text-center text-xs font-bold text-gray-400">
        {dayNames.map(day => <div key={day} className="py-2">{day}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {Array.from({ length: startOffset }).map((_, i) => <div key={`empty-${i}`} className="py-2" />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const hasEvent = eventDates.includes(dateStr);
          const isSelected = selectedDate && selectedDate.toISOString().split('T')[0] === dateStr;

          return (
            <button
              key={day}
              onClick={() => onDateSelect(isSelected ? null : new Date(dateStr))}
              className={`relative py-3 rounded-xl text-sm font-medium transition-all ${isSelected ? 'bg-brand-purple-900 text-white shadow-md scale-105' : 'text-gray-700 hover:bg-brand-purple-50'} ${hasEvent && !isSelected ? 'font-bold' : ''}`}
            >
              {day}
              {hasEvent && (
                <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-brand-green-500'}`}></span>
              )}
            </button>
          );
        })}
      </div>
      
      {selectedDate && (
        <button onClick={() => onDateSelect(null)} className="w-full mt-6 py-2 text-xs font-bold text-gray-400 hover:text-brand-purple-900 transition-colors uppercase tracking-widest">
          {t('news_page.clear_filter')}
        </button>
      )}
    </div>
  );
};

export default CustomCalendar;
